import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Plus } from 'lucide-react'

import {
  allocateDepartmentBudget,
  fundProductionBudget,
  getProductionBudget,
  reclaimDepartmentBudget,
  type ProductionBudgetView,
  type ProductionDepartment,
} from '@/api/studio-production'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import { ProtoBtn, ProtoInput, ProtoLabel, ProtoListTable, ProtoModal, ProtoSelect } from '@/features/studio/components/ui-kit'

export function ProductionBudgetPanel({
  productionId,
  isOwner,
  departments: departmentsProp,
}: {
  productionId: string
  isOwner: boolean
  departments: ProductionDepartment[]
}) {
  const departments = departmentsProp ?? []
  const { t } = useTranslation()
  const [budget, setBudget] = useState<ProductionBudgetView | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fundOpen, setFundOpen] = useState(false)
  const [allocateOpen, setAllocateOpen] = useState(false)
  const [fundAmount, setFundAmount] = useState('1000')
  const [fundNote, setFundNote] = useState('')
  const [allocDeptId, setAllocDeptId] = useState('')
  const [allocAmount, setAllocAmount] = useState('500')
  const [allocNote, setAllocNote] = useState('')
  const [busy, setBusy] = useState(false)

  const reloadBudget = useCallback(async () => {
    if (!productionId) return
    setLoading(true)
    setError(null)
    try {
      const res = await getProductionBudget(productionId)
      setBudget(res)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'budget.loadFailed'))
      setBudget(null)
    } finally {
      setLoading(false)
    }
  }, [productionId])

  useEffect(() => {
    void reloadBudget()
  }, [reloadBudget])

  async function handleFund() {
    const amount = Math.floor(Number(fundAmount))
    if (!Number.isFinite(amount) || amount <= 0) {
      setError(t('budget.amountInvalid'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const res = await fundProductionBudget(productionId, {
        amount,
        note: fundNote.trim() || undefined,
      })
      setBudget(res)
      setFundOpen(false)
      setFundAmount('1000')
      setFundNote('')
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'budget.fundFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function handleAllocate() {
    const amount = Math.floor(Number(allocAmount))
    if (!allocDeptId) {
      setError(t('budget.deptRequired'))
      return
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setError(t('budget.amountInvalid'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const res = await allocateDepartmentBudget(productionId, {
        department_id: allocDeptId,
        amount,
        note: allocNote.trim() || undefined,
      })
      setBudget(res)
      setAllocateOpen(false)
      setAllocAmount('500')
      setAllocNote('')
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'budget.allocateFailed'))
    } finally {
      setBusy(false)
    }
  }

  async function handleReclaim(deptId: string, maxAmount: number) {
    const raw = window.prompt(t('budget.reclaimPrompt', { max: maxAmount }))
    if (raw == null) return
    const amount = Math.floor(Number(raw))
    if (!Number.isFinite(amount) || amount <= 0 || amount > maxAmount) {
      setError(t('budget.amountInvalid'))
      return
    }
    setBusy(true)
    setError(null)
    try {
      const res = await reclaimDepartmentBudget(productionId, { department_id: deptId, amount })
      setBudget(res)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'budget.reclaimFailed'))
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-amber-400" />
      </div>
    )
  }

  return (
    <>
      {error ? <p className="mb-3 text-sm text-rose-300">{t(error, { defaultValue: error })}</p> : null}
      {!budget ? (
        <p className="py-8 text-center text-xs text-zinc-500">{t('budget.empty')}</p>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-xs text-zinc-500">{t('budget.poolBalance')}</p>
              <p className="text-2xl font-semibold text-amber-200">{budget.pool_balance}</p>
              <p className="mt-0.5 text-[10px] text-zinc-600">{t('budget.fundedTotal', { n: budget.total_funded })}</p>
            </div>
            {isOwner ? (
              <div className="flex flex-wrap gap-2">
                <ProtoBtn size="sm" onClick={() => setFundOpen(true)}>
                  <Plus className="h-3.5 w-3.5" />
                  {t('budget.fund')}
                </ProtoBtn>
                {departments.length > 0 ? (
                  <ProtoBtn size="sm" variant="secondary" onClick={() => setAllocateOpen(true)}>
                    {t('budget.allocateDept')}
                  </ProtoBtn>
                ) : null}
              </div>
            ) : null}
          </div>

          {(budget.departments ?? []).length > 0 ? (
            <ProtoListTable>
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-white/[0.06] bg-[#0e0e16] text-xs text-zinc-500">
                  <tr>
                    <th className="px-4 py-3">{t('budget.deptCol')}</th>
                    <th className="px-4 py-3">{t('budget.deptBalanceCol')}</th>
                    {isOwner ? <th className="px-4 py-3 text-right">{t('team.colActions')}</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {(budget.departments ?? []).map((d) => (
                    <tr key={d.department_id} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                      <td className="px-4 py-3 text-zinc-300">{d.department_name || d.department_id}</td>
                      <td className="px-4 py-3 text-amber-200">{d.sub_pool_balance}</td>
                      {isOwner ? (
                        <td className="px-4 py-3 text-right">
                          {d.sub_pool_balance > 0 ? (
                            <ProtoBtn
                              size="sm"
                              variant="ghost"
                              disabled={busy}
                              onClick={() => void handleReclaim(d.department_id, d.sub_pool_balance)}
                            >
                              {t('budget.reclaim')}
                            </ProtoBtn>
                          ) : (
                            <span className="text-zinc-600">—</span>
                          )}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </ProtoListTable>
          ) : null}
        </>
      )}

      <ProtoModal
        open={fundOpen}
        onClose={() => setFundOpen(false)}
        title={t('budget.fundTitle')}
        footer={
          <>
            <ProtoBtn size="sm" variant="secondary" onClick={() => setFundOpen(false)} disabled={busy}>
              {t('common.cancel')}
            </ProtoBtn>
            <ProtoBtn size="sm" disabled={busy} onClick={() => void handleFund()}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t('budget.fundConfirm')}
            </ProtoBtn>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <ProtoLabel required>{t('budget.amount')}</ProtoLabel>
            <ProtoInput
              type="number"
              min={1}
              className="mt-1.5"
              value={fundAmount}
              onChange={(e) => setFundAmount(e.target.value)}
            />
          </div>
          <div>
            <ProtoLabel>{t('budget.note')}</ProtoLabel>
            <ProtoInput className="mt-1.5" value={fundNote} onChange={(e) => setFundNote(e.target.value)} />
          </div>
        </div>
      </ProtoModal>

      <ProtoModal
        open={allocateOpen}
        onClose={() => setAllocateOpen(false)}
        title={t('budget.allocateTitle')}
        footer={
          <>
            <ProtoBtn size="sm" variant="secondary" onClick={() => setAllocateOpen(false)} disabled={busy}>
              {t('common.cancel')}
            </ProtoBtn>
            <ProtoBtn size="sm" disabled={busy} onClick={() => void handleAllocate()}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {t('budget.allocateConfirm')}
            </ProtoBtn>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <ProtoLabel required>{t('budget.dept')}</ProtoLabel>
            <ProtoSelect className="mt-1.5" value={allocDeptId} onChange={(e) => setAllocDeptId(e.target.value)}>
              <option value="">{t('budget.pickDept')}</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </ProtoSelect>
          </div>
          <div>
            <ProtoLabel required>{t('budget.amount')}</ProtoLabel>
            <ProtoInput
              type="number"
              min={1}
              className="mt-1.5"
              value={allocAmount}
              onChange={(e) => setAllocAmount(e.target.value)}
            />
            {budget ? (
              <p className="mt-1 text-[10px] text-zinc-500">
                {t('budget.availableBalance', { n: budget.pool_balance })}
              </p>
            ) : null}
          </div>
          <div>
            <ProtoLabel>{t('budget.note')}</ProtoLabel>
            <ProtoInput className="mt-1.5" value={allocNote} onChange={(e) => setAllocNote(e.target.value)} />
          </div>
        </div>
      </ProtoModal>
    </>
  )
}
